// This schema file is the schema definition of all the schema validations being done using joi.

import Joi from 'joi';
import { JourneyListApiResponse } from './TypeDefs';
/**
 * @method journeyListSchema
 * @description schema validation for journey list api response
 */
export const journeyListSchema = Joi.object<JourneyListApiResponse>({
  totalJourneysCount: Joi.number().required(),
  journeyList: Joi.array().items(
    Joi.object(
      {
        status: Joi.string().required(),
        version: Joi.number().required(),
        updatedBy: Joi.string().optional(),
        updatedTimestamp: Joi.string().optional(),
        journeyName: Joi.string().required().allow(''),
        journeyCategory: Joi.string().optional().allow(''),
        journeyStatusHistory: Joi.array().items(
          Joi.object({
            comments: Joi.string().optional().allow(''),
            description: Joi.string().optional().allow(''),
          })
        ).optional(),
      }
    ).optional()
  )
}).options({ stripUnknown: true });

/**
 * @method createJourneyFormSchema
 * @description schema definition for create Journey form field.
 */
export const createJourneyFormSchema = Joi.object({
  journeyName: Joi.string()
    .min(1)
    .max(40)
    .pattern(/^[a-zA-Z]([a-zA-Z0-9-])*$/)
    .required(),
  journeyDescription: Joi.string().allow('').optional(),
  journeyType: Joi.string().optional(),
  createdBy: Joi.string().optional()
});

const viewJourneyEdgeSchema = Joi.object({
  ingress: Joi.array().items({ nodeId: Joi.string() }).required(),
  egress: Joi.array().items({
    nodeId: Joi.string().required(),
    mep: Joi.string().optional(),
    external: Joi.boolean().required(),
    nextStep: Joi.string().optional(),
    conditionMap: Joi.object().pattern(
      Joi.string(),
      Joi.object().keys({
        builderExpression: Joi.string().required().allow(''),
        rawExpression: Joi.string().required(),
      }).required()
    ).optional(),
  }).required(),
  ingressOperation: Joi.string().optional(),
  payloadTransformations: Joi.array().items({
    egress: Joi.string().required(),
    ingress: Joi.string().required(),
    persistentAttributes: Joi.array().items(Joi.string().required()).required()
  }).optional()
});

const viewJourneyNodeSchema = Joi.object({
  nodeId: Joi.string().required(),
  serviceName: Joi.string().optional(),
  serviceDescription: Joi.string().optional().allow(''),
  serviceType: Joi.string().optional(),
  serviceVersion: Joi.number().optional(),
  serviceOperationName: Joi.string().optional(),
  eventName: Joi.string().required(),
  eventVersion: Joi.number().required(),
  isCompensateAction: Joi.boolean().optional(),
  compensatoryEventMapName: Joi.string().optional(),
  status: Joi.string().optional(),
  xCoordinate: Joi.number().optional(),
  yCoordinate: Joi.number().optional()
});

const viewJourneyCompEdgeSchema = Joi.object({
  ingress: Joi.array().items({ nodeId: Joi.string() }).required(),
  egress: Joi.array().items({
    nodeId: Joi.string().required(),
    mep: Joi.string().optional(),
    external: Joi.boolean().required(),
    nextStep: Joi.string().optional(),
    conditionMap: Joi.object().pattern(
      Joi.string(),
      Joi.object().keys({
        builderExpression: Joi.string().required(),
        rawExpression: Joi.string().required(),
      }).required()
    ).optional(),
  }).required(),
  ingressOperation: Joi.string().optional(),
  payloadTransformations: Joi.array().items({
    egress: Joi.string().required(),
    ingress: Joi.string().required(),
    persistentAttributes: Joi.array().items(Joi.string().required()).required()
  }).optional()
});

const viewJourneyCompNodeSchema = Joi.object({
  nodeId: Joi.string().required(),
  serviceName: Joi.string().optional(),
  serviceDescription: Joi.string().optional().allow(''),
  serviceType: Joi.string().optional(),
  serviceVersion: Joi.number().optional(),
  serviceOperationName: Joi.string().optional(),
  eventName: Joi.string().required(),
  eventVersion: Joi.number().required(),
  isCompensateAction: Joi.boolean().optional(),
  compensatoryEventMapName: Joi.string().optional(),
  status: Joi.string().optional(),
  xCoordinate: Joi.number().optional(),
  yCoordinate: Joi.number().optional()
});

const createJourneyStepEdge = Joi.object().keys({
  stepIngress: Joi.array().items(
    Joi.object().keys({
      stepNodeId: Joi.string().required(),
    })
  ).required(),
  stepEgress: Joi.array().items(
    Joi.object().keys({
      stepNodeId: Joi.string().required(),
      eventName: Joi.string().optional(),
    })
  ).required(),
});

const createJourneyStepNode = Joi.object().keys({
  stepNodeId: Joi.string().required(),
  stepName: Joi.string().required(),
  stepDescription: Joi.string().required().allow(''),
  stepAction: Joi.string().optional(),
  startNode: Joi.object().keys({ viewJourneyCompNodeSchema: viewJourneyNodeSchema }).optional(),
  eventsCount: Joi.number().required(),
  servicesCount: Joi.number().required(),
  cemCount: Joi.number().required(),
  xCoordinate: Joi.number().required(),
  yCoordinate: Joi.number().required(),
});

const journeyComments = Joi.object().keys({
  journeyStatusHistoryUUID: Joi.string().optional(),
  comments: Joi.string().optional().allow(''),
  status: Joi.string().required(),
  createdBy: Joi.string().required(),
  createdTimestamp: Joi.string().required(),
});

/**
 * @method createJourneyApiSchema
 * @description schema definition for create Journey api response.
 */
export const createJourneySchema = Joi.object({
  uuid: Joi.string().required(),
  journeyName: Joi.string().required(),
  journeyDescription: Joi.string().required().allow(''),
  journeyType: Joi.string().optional().allow(''),
  journeyCategory: Joi.string().optional().allow(''),
  version: Joi.number().required(),
  status: Joi.string().required(),
  journeyJsonDetails: Joi.object().keys({
    stepNodes: Joi.array().items(createJourneyStepNode).required(),
    stepEdges: Joi.array().items(createJourneyStepEdge).required(),
  }).required(),
  stepDetails: Joi.object().pattern(
    Joi.string(),
    Joi.object().keys({
      eventMapName: Joi.string(),
      eventMapMaxExecutionTimeInSec: Joi.alternatives().try(Joi.number(), Joi.allow(null)).required(),
      uniqueIdentifier: Joi.string(),
      eventMapNodes: Joi.array().items(viewJourneyNodeSchema).required(),
      eventMapEdges: Joi.array().items(viewJourneyEdgeSchema).required(),
    })
  ),
  createdBy: Joi.string().optional(),
  createdTimeStamp: Joi.string().optional().allow(''),
  updatedBy: Joi.string().optional(),
  updatedTimeStamp: Joi.string().optional().allow(''),
  journeyStatusHistory: Joi.array().items(journeyComments).optional(),
}).options({ stripUnknown: true });

/**
 * @method getJourneySchema
 * @description schema definition for get Journey api response.
 */
export const getJourneySchema = Joi.array()
  .items(
    Joi.object({
      uuid: Joi.string().required(),
      journeyName: Joi.string().required(),
      journeyDescription: Joi.string().required().allow(''),
      journeyType: Joi.string().optional().allow(''),
      journeyCategory: Joi.string().optional().allow(''),
      version: Joi.number().required(),
      status: Joi.string().required(),
      journeyJsonDetails: Joi.object().keys({
        stepNodes: Joi.array().items(createJourneyStepNode).required(),
        stepEdges: Joi.array().items(createJourneyStepEdge).required(),
      }).required(),
      stepDetails: Joi.object().pattern(
        Joi.string(),
        Joi.object().keys({
          eventMapName: Joi.string(),
          eventMapMaxExecutionTimeInSec: Joi.alternatives().try(Joi.number(), Joi.allow(null)).required(),
          uniqueIdentifier: Joi.string(),
          eventMapNodes: Joi.array().items(viewJourneyNodeSchema).required(),
          eventMapEdges: Joi.array().items(viewJourneyEdgeSchema).required(),
        })
      ),
      createdBy: Joi.string().optional(),
      createdTimeStamp: Joi.string().optional().allow(''),
      updatedBy: Joi.string().optional(),
      updatedTimeStamp: Joi.string().optional().allow(''),
      journeyStatusHistory: Joi.array().items(journeyComments).optional(),
    })
  ).options({ stripUnknown: true });

export const saveCEMSchema = Joi.object({
  eventMapDetails: {
    eventMapNodes: Joi.array().items({
      nodeId: Joi.string().optional(),
      compensatoryEventMapName: Joi.string().optional()
    }).optional()
  }
}).options({ stripUnknown: true });

/**
 * @method getAllServicesSchema
 * @description schema definition for get all services api response.
 */
export const getAllServicesSchema = Joi.object({
  currentPageNumber: Joi.number().required(),
  numberOfRecordsPerPage: Joi.number().required(),
  totalRecords: Joi.number().required(),
  numberOfPages: Joi.number().required(),
  services: Joi.array().items({
    uuid: Joi.string().required(),
    serviceName: Joi.string().required(),
    serviceDescription: Joi.string().allow('').optional(),
    serviceType: Joi.string().optional(),
    status: Joi.string().required(),
    version: Joi.number().required(),
    createdBy: Joi.string().required(),
    createdTimeStamp: Joi.string().required(),
    updatedBy: Joi.string().required(),
    updatedTimeStamp: Joi.string().required()
  })
}).options({ stripUnknown: true });

/**
 * @method associateServiceSchema
 * @description schema definition for associated services api response.
 */
export const associateServiceSchema = Joi.array()
  .items(
    Joi.object({
      serviceName: Joi.string().required(),
      serviceDescription: Joi.string().optional().allow(''),
      serviceType: Joi.string().optional(),
      serviceOperationName: Joi.string().required(),
      serviceOperationDescription: Joi.string().optional(),
      ingressEventName: Joi.string().required()
    })
  )
  .options({ stripUnknown: true });

/**
 * @method getServiceOperationsSchema
 * @description schema validation for get Service Operations api response
 */
export const getServiceOperationsSchema = Joi.object({
  serviceName: Joi.string().required(),
  serviceDescription: Joi.string().optional().allow(''),
  serviceOperations: Joi.array()
    .items({
      serviceOperation: Joi.string().required(),
      ingressEvent: Joi.string().optional(),
    })
    .required()
}).options({ stripUnknown: true });

/**
 * @method getCompensatoryEventMapDetailsSchema
 * @description schema validation for get compensatory eventMap details api response
 */
export const getCompensatoryEventMapDetailsSchema = Joi.array().items(
  Joi.object({
    eventMapName: Joi.string().required(),
    eventMapMaxExecutionTimeInSec: Joi.number().optional(),
    createdBy: Joi.string().required(),
    createdTimestamp: Joi.string().required(),
    updatedBy: Joi.string().required(),
    updatedTimestamp: Joi.string().required(),
    eventMapNodes: Joi.array().items(viewJourneyCompNodeSchema).required(),
    eventMapEdges: Joi.array().items(viewJourneyCompEdgeSchema).required()
  })
).options({ stripUnknown: true });

export const deleteCompensatoryMapSchema = Joi.number().required();

export const validateStepSchema = Joi.object({
  journeyStepName: Joi.string().required(),
  totalServicesCount: Joi.number().required(),
  totalEventsCount: Joi.number().required(),
  totalCEMsCount: Joi.number().required()
});

export const importJourneyConfiguration = Joi.string().required();

export const createScheduleApiSchema = Joi.object({
  version: Joi.number(),
  jobId: Joi.string().required(),
  jobName: Joi.string().min(1).max(50).pattern(/^[a-zA-Z]([a-zA-Z0-9-])*$/)
    .required(),
  jobType: Joi.string().required()
});

export const scheduledJobListApiSchema = Joi.object({
  totalJobCount: Joi.number().required(),
  jobDetails: Joi.array().items(
    Joi.object({
      jobId: Joi.string().required(),
      jobName: Joi.string().required(),
      jobDescription: Joi.string().optional(),
      frequency: Joi.string().optional(),
      jobStatus: Joi.string().required(),
      journeyName: Joi.string().required(),
      updatedTimestamp: Joi.string().required()
    })
  )
}).options({ stripUnknown: true });
/**
 * @method UpdateStatusAPISchema
 * @description schema validation for update status api patch response
 */
export const updateStatusAPISchema = Joi.object({
  status: Joi.string().required(),
}).options({ stripUnknown: true });

/**
 * @method MultiSelectOptionsAPISchema
 * @description schema validation for multi-select options api response
 */
export const eventSchemaDetailSchema = Joi.array().items({
  schema: Joi.string().required()
}).options({ stripUnknown: true });

export const deleteSchedulerSchema = Joi.number().required();
